import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import Keycloak from "keycloak-js";
import { App } from "./app/App";
import { AuthBootError, authBootOutcome } from "./app/authBoot";
import { startTokenRefresh } from "./app/session";
import { clearTokens, loadTokens, saveTokens } from "./app/tokenCache";
import { initI18n } from "./app/i18n";
import { useTelemetryStore } from "./stores/telemetryStore";
import { useUIStore } from "./stores/uiStore";
import { useSiteStore } from "./stores/siteStore";

initI18n();

// r3f 9.x uses THREE.Clock internally; three.js 0.175+ deprecated it in favour
// of THREE.Timer but r3f hasn't migrated yet. Suppress the one-time warning so
// it doesn't pollute the console until r3f ships the fix.
{
  const _warn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].includes("THREE.Clock")) return;
    _warn(...args);
  };
}

// Expose stores for Playwright E2E in dev/test builds only.
if (import.meta.env.DEV) {
  (window as any).__testStore = useTelemetryStore;
  (window as any).__uiStore = useUIStore;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/**
 * Keycloak sits beside Vite on the dev machine, so the default follows the
 * browser's own origin instead of a literal `localhost`.
 *
 * The literal was only ever correct when the browser ran ON that machine: open
 * the dashboard from a phone at http://<lan-ip>:4200 and `localhost:8088` is the
 * PHONE, which has no Keycloak — the handshake fails and (before this) the shell
 * degraded into something that read as a permissions bug (ADR 156).
 *
 * Scheme carries the port because the dev stack publishes Keycloak on both: 8088
 * plain, 8443 TLS (`make up-tls`, ADR 157). An https page may not talk to an http
 * Keycloak anyway — mixed content — so the two always move together. Deployments
 * set VITE_KEYCLOAK_URL explicitly and are unaffected.
 */
const keycloakUrl =
  import.meta.env.VITE_KEYCLOAK_URL ??
  (window.location.protocol === "https:"
    ? `https://${window.location.hostname}:8443`
    : `http://${window.location.hostname}:8088`);

const keycloak = new Keycloak({
  url:      keycloakUrl,
  realm:    "hunter-twin",
  clientId: "web-dashboard",
});

// ADR 358: warm the auth origin's TCP+TLS while the rest of boot runs — on the
// demo path that handshake alone is several hundred ms. Dynamic because the
// origin is derived above; index.html cannot know it.
{
  const pre = document.createElement("link");
  pre.rel = "preconnect";
  pre.href = keycloakUrl;
  document.head.appendChild(pre);
}

async function boot() {
  // Security is on by default (ADR 112), so require a real Keycloak login: with
  // `login-required` an unauthenticated user is redirected to Keycloak and comes back
  // with a token. Set VITE_REQUIRE_AUTH=false for a no-auth dev run (paired with
  // TWIN_SECURITY_DISABLED=true on the backend), which keeps the silent check-sso path.
  const requireAuth = import.meta.env.VITE_REQUIRE_AUTH !== "false";
  let authed = false;
  let bootError: unknown;
  try {
    // ADR 358: seed init with the tab's previous token trio. With them,
    // keycloak-js authenticates locally (refreshing over ONE silent POST when
    // the access token has expired) instead of the login-required double
    // navigation through the auth origin; when the refresh token is dead it
    // falls back to that redirect exactly as before. checkLoginIframe off:
    // the status iframe is one more serial auth-origin fetch at init, and the
    // ADR 318 refresh loop already owns session-death detection.
    const cached = loadTokens();
    const authenticated = await keycloak.init({
      onLoad: requireAuth ? "login-required" : "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false,
      // The silent-SSO iframe belongs to the check-sso (no-auth dev) path
      // only. Naming it under login-required made keycloak-js run its
      // third-party-cookie probe (3p-cookies/step1+2) — two serial iframe
      // loads through the auth origin, ~1,2 s on the demo path (ADR 358).
      ...(requireAuth ? {} : { silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html" }),
      ...(cached ?? {}),
    });
    if (authenticated && keycloak.tokenParsed && keycloak.token) {
      saveTokens(keycloak);
      // Await: this resolves the accessible sites and snaps activeSiteId into range
      // BEFORE first render — otherwise the app boots on the fallback site and a user
      // scoped elsewhere fires a burst of wrong-site requests that 403.
      await useSiteStore.getState().initFromToken(keycloak.tokenParsed as any, keycloak.token);
      const parsed = keycloak.tokenParsed as { preferred_username?: string; email?: string };
      // Expose identity + logout for the app shell (non-React helper, mirrors the token
      // publish pattern). keycloak lives in this module; the shell just needs these.
      (window as any).__auth = {
        username: parsed.preferred_username ?? parsed.email ?? "",
        logout: () => { clearTokens(); keycloak.logout({ redirectUri: window.location.origin }); },
      };
      // ADR 318: keep that token alive. Without this the tab dies quietly at the
      // 900 s access-token lifespan — every call 401s and the UI reads the
      // failures as an empty system until someone presses F5.
      startTokenRefresh(keycloak, {
        onToken: (raw) => { useSiteStore.getState().setToken(raw); saveTokens(keycloak); },
        // The refresh token is gone too (SSO idle/max lifespan, or the session was
        // ended elsewhere), so there is nothing left to renew silently. Send the
        // user back through Keycloak on the URL they are on: with a live SSO
        // session that is an invisible round trip, and without one it is the login
        // screen — which is the honest answer, not a shell full of 401s.
        onSessionEnded: () => { clearTokens(); keycloak.login({ redirectUri: window.location.href }); },
      });
      authed = true;
    }
  } catch (e) {
    // A stale cached trio must never be able to brick boot twice — drop it so
    // the next reload takes the clean redirect path.
    clearTokens();
    bootError = e;
  }

  const root = document.getElementById("root");
  if (!root) throw new Error("missing #root");

  // Security is on by default (ADR 112), so a failed handshake is fatal — say so
  // rather than degrade. Carrying on unauthenticated used to render a shell that
  // was indistinguishable from a permissions problem (see authBoot.tsx), which
  // is a lie about the user's account and cost real debugging time.
  if (authBootOutcome(requireAuth, authed) === "block") {
    createRoot(root).render(
      <StrictMode>
        <AuthBootError keycloakUrl={keycloakUrl} detail={messageOf(bootError)} />
      </StrictMode>,
    );
    return;
  }

  // Deliberate no-auth dev run (VITE_REQUIRE_AUTH=false): initFromToken never ran,
  // so the site store has no warehouses loaded and activeWarehouseId stays
  // undefined — which blocks the mapping flow (and leaves the warehouse picker
  // empty). Load sites + warehouses for the fallback site; the store auto-selects
  // the warehouse when the site has exactly one.
  if (!authed) {
    const store = useSiteStore.getState();
    store.loadAvailableSites().finally(() => {
      const s = useSiteStore.getState();
      s.loadAvailableWarehouses(s.activeSiteId);
    });
  }

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

/** keycloak-js rejects with plain objects as often as with Errors. */
function messageOf(e: unknown): string | undefined {
  if (!e) return undefined;
  if (e instanceof Error) return e.message;
  const s = String(e);
  return s === "[object Object]" ? undefined : s;
}

boot();
